import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { OutlineLine } from './outline';

import { MaterialSymbol } from '@/components/MaterialSymbol';
import { MicroLabel } from '@/components/ui';

/** Indent per level in the preview — tighter than the tree's, it is a summary. */
const PREVIEW_INDENT_PX = 18;

export type OutlinePreviewProps = {
  lines: readonly OutlineLine[];
};

/**
 * The structure the text on the left will become. Deliberately a preview and not
 * an editor: it makes the depth rules — and any flattening at the BR-23 ceiling —
 * visible BEFORE the outline is committed.
 */
export const OutlinePreview = ({ lines }: OutlinePreviewProps) => {
  const { t } = useTranslation('pmPlanning');
  return (
    <Box
      data-automation-id="capture-preview"
      sx={{
        width: 340,
        flex: 'none',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        bgcolor: 'var(--bg-sunken)',
        borderLeft: '1px solid var(--border-divider)',
      }}
    >
      <Box sx={{ px: 2, pt: 1.5, pb: 1, borderBottom: '1px solid var(--border-divider)' }}>
        <MicroLabel>{t('capture.PreviewTitle')}</MicroLabel>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, py: 1.5 }}>
        {lines.length === 0 ? (
          <Typography
            variant="label"
            component="p"
            sx={{ px: 1.5, py: 3.5, textAlign: 'center', color: 'var(--fg-3)', lineHeight: 1.6 }}
          >
            {t('capture.PreviewEmpty')}
          </Typography>
        ) : (
          lines.map((line, index) => (
            <Box
              // Lines have no identity of their own; position IS the identity here.
              key={index}
              data-automation-id="capture-preview-row"
              data-depth={line.depth}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.875,
                px: 0.75,
                py: 0.5,
                pl: `${line.depth * PREVIEW_INDENT_PX + 6}px`,
                minWidth: 0,
              }}
            >
              {line.depth === 0 ? (
                <MaterialSymbol name="folder" size={16} color="var(--color-pink-80)" />
              ) : (
                <Box
                  sx={{
                    width: 5,
                    height: 5,
                    flex: 'none',
                    borderRadius: '50%',
                    bgcolor: 'var(--color-fog-60)',
                    mx: 0.625,
                  }}
                />
              )}
              <Typography
                variant={line.depth === 0 ? 'bodyBold' : 'bodyRegular'}
                sx={{
                  color: line.depth === 0 ? 'var(--fg-1)' : 'var(--fg-2)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {line.name}
              </Typography>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
};
